import { InfoIcon, AlertCircleIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className='space-y-4'>
      <div>
        <h2 className='text-lg font-semibold'>{title}</h2>
        <Separator className='mt-2' />
      </div>
      {children}
    </section>
  )
}

export function ComponentsPage() {
  return (
    <main>
      <div className='container py-10 space-y-12'>
        {/* Buttons */}
        <Section title='Button'>
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>Variants</p>
            <div className='flex flex-wrap gap-3'>
              <Button>Default</Button>
              <Button variant='secondary'>Secondary</Button>
              <Button variant='destructive'>Destructive</Button>
              <Button variant='outline'>Outline</Button>
              <Button variant='ghost'>Ghost</Button>
              <Button variant='link'>Link</Button>
            </div>
          </div>
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>Sizes</p>
            <div className='flex flex-wrap items-center gap-3'>
              <Button size='sm'>Small</Button>
              <Button size='default'>Default</Button>
              <Button size='lg'>Large</Button>
              <Button size='icon' aria-label='icon'>
                <InfoIcon />
              </Button>
            </div>
          </div>
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>States</p>
            <div className='flex flex-wrap gap-3'>
              <Button disabled>Disabled</Button>
              <Button variant='outline' disabled>
                Disabled Outline
              </Button>
            </div>
          </div>
        </Section>

        {/* Badge */}
        <Section title='Badge'>
          <div className='flex flex-wrap gap-3'>
            <Badge>Default</Badge>
            <Badge variant='secondary'>Secondary</Badge>
            <Badge variant='destructive'>Destructive</Badge>
            <Badge variant='outline'>Outline</Badge>
          </div>
        </Section>

        {/* Alert */}
        <Section title='Alert'>
          <Alert>
            <InfoIcon className='h-4 w-4' />
            <AlertTitle>Information</AlertTitle>
            <AlertDescription>
              This is an informational alert. It provides context or guidance to
              the user.
            </AlertDescription>
          </Alert>
          <Alert variant='destructive'>
            <AlertCircleIcon className='h-4 w-4' />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Something went wrong. Please check the details and try again.
            </AlertDescription>
          </Alert>
        </Section>

        {/* Form Controls */}
        <Section title='Form Controls'>
          <div className='grid gap-6 md:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='email'>Input</Label>
              <Input id='email' type='email' placeholder='you@example.com' />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='disabled-input'>Disabled Input</Label>
              <Input
                id='disabled-input'
                placeholder='Cannot edit'
                disabled
              />
            </div>
            <div className='space-y-2 md:col-span-2'>
              <Label htmlFor='bio'>Textarea</Label>
              <Textarea
                id='bio'
                placeholder='Write something about yourself…'
                rows={3}
              />
            </div>
            <div className='space-y-3'>
              <Label>Checkbox</Label>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Checkbox id='terms' />
                  <Label htmlFor='terms' className='font-normal cursor-pointer'>
                    Accept terms and conditions
                  </Label>
                </div>
                <div className='flex items-center gap-2'>
                  <Checkbox id='newsletter' defaultChecked />
                  <Label
                    htmlFor='newsletter'
                    className='font-normal cursor-pointer'
                  >
                    Subscribe to newsletter
                  </Label>
                </div>
                <div className='flex items-center gap-2'>
                  <Checkbox id='disabled-check' disabled />
                  <Label
                    htmlFor='disabled-check'
                    className='font-normal text-muted-foreground'
                  >
                    Disabled option
                  </Label>
                </div>
              </div>
            </div>
            <div className='space-y-3'>
              <Label>Switch</Label>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Switch id='notifications' />
                  <Label
                    htmlFor='notifications'
                    className='font-normal cursor-pointer'
                  >
                    Enable notifications
                  </Label>
                </div>
                <div className='flex items-center gap-2'>
                  <Switch id='dark-mode' defaultChecked />
                  <Label
                    htmlFor='dark-mode'
                    className='font-normal cursor-pointer'
                  >
                    Dark mode
                  </Label>
                </div>
                <div className='flex items-center gap-2'>
                  <Switch id='disabled-switch' disabled />
                  <Label
                    htmlFor='disabled-switch'
                    className='font-normal text-muted-foreground'
                  >
                    Disabled
                  </Label>
                </div>
              </div>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='role'>Select</Label>
              <Select>
                <SelectTrigger id='role'>
                  <SelectValue placeholder='Select a role' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='admin'>Admin</SelectItem>
                  <SelectItem value='editor'>Editor</SelectItem>
                  <SelectItem value='viewer'>Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        {/* Card */}
        <Section title='Card'>
          <div className='grid gap-4 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
                <CardDescription>
                  Manage your account preferences and profile details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='space-y-2'>
                  <Label htmlFor='username'>Username</Label>
                  <Input id='username' defaultValue='john_doe' />
                </div>
              </CardContent>
              <CardFooter className='flex justify-end gap-2'>
                <Button variant='outline'>Cancel</Button>
                <Button>Save Changes</Button>
              </CardFooter>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
                <CardDescription>Your activity this month.</CardDescription>
              </CardHeader>
              <CardContent className='space-y-3'>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Projects</span>
                  <span className='font-semibold'>12</span>
                </div>
                <Separator />
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Tasks completed</span>
                  <span className='font-semibold'>48</span>
                </div>
                <Separator />
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Hours logged</span>
                  <span className='font-semibold'>134</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* Avatar */}
        <Section title='Avatar'>
          <div className='flex flex-wrap items-end gap-4'>
            <div className='space-y-1 text-center'>
              <Avatar className='h-16 w-16'>
                <AvatarImage src='https://github.com/shadcn.png' alt='shadcn' />
                <AvatarFallback>SC</AvatarFallback>
              </Avatar>
              <p className='text-xs text-muted-foreground'>With image</p>
            </div>
            <div className='space-y-1 text-center'>
              <Avatar className='h-12 w-12'>
                <AvatarFallback>JD</AvatarFallback>
              </Avatar>
              <p className='text-xs text-muted-foreground'>Fallback</p>
            </div>
            <div className='space-y-1 text-center'>
              <Avatar className='h-8 w-8'>
                <AvatarFallback>AB</AvatarFallback>
              </Avatar>
              <p className='text-xs text-muted-foreground'>Small</p>
            </div>
          </div>
        </Section>

        {/* Accordion */}
        <Section title='Accordion'>
          <Accordion type='single' collapsible className='w-full max-w-xl'>
            <AccordionItem value='item-1'>
              <AccordionTrigger>What is shadcn/ui?</AccordionTrigger>
              <AccordionContent>
                shadcn/ui is a collection of re-usable components built with
                Radix UI and Tailwind CSS. Components are copied into your
                project so you own and can customize them.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value='item-2'>
              <AccordionTrigger>Is it accessible?</AccordionTrigger>
              <AccordionContent>
                Yes. All components are built on top of Radix UI primitives,
                which are fully accessible by default — keyboard navigation,
                focus management, and ARIA attributes included.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value='item-3'>
              <AccordionTrigger>Can I customize the styles?</AccordionTrigger>
              <AccordionContent>
                Absolutely. Since the components live in your codebase, you can
                modify them freely. The design tokens (colors, spacing, radius)
                are driven by CSS variables in your stylesheet.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Section>

        {/* Tabs */}
        <Section title='Tabs'>
          <Tabs defaultValue='overview' className='w-full max-w-xl'>
            <TabsList>
              <TabsTrigger value='overview'>Overview</TabsTrigger>
              <TabsTrigger value='analytics'>Analytics</TabsTrigger>
              <TabsTrigger value='settings'>Settings</TabsTrigger>
            </TabsList>
            <TabsContent value='overview' className='mt-4'>
              <Card>
                <CardContent className='pt-4 text-sm text-muted-foreground'>
                  Overview tab content. Summarize key information and recent
                  activity here.
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value='analytics' className='mt-4'>
              <Card>
                <CardContent className='pt-4 text-sm text-muted-foreground'>
                  Analytics tab content. Charts and metrics would live here.
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value='settings' className='mt-4'>
              <Card>
                <CardContent className='pt-4 text-sm text-muted-foreground'>
                  Settings tab content. Configuration options would live here.
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </Section>

        {/* Dialog */}
        <Section title='Dialog'>
          <div className='flex gap-3'>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant='outline'>Open Dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Action</DialogTitle>
                  <DialogDescription>
                    This action cannot be undone. Make sure you want to proceed
                    before confirming.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant='outline'>Cancel</Button>
                  <Button>Confirm</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant='destructive'>Delete Item</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Item</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this item? This action is
                    permanent and cannot be reversed.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant='outline'>Cancel</Button>
                  <Button variant='destructive'>Delete</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </Section>

        {/* Separator */}
        <Section title='Separator'>
          <div className='space-y-4 max-w-sm'>
            <p className='text-sm'>Horizontal separator</p>
            <Separator />
            <p className='text-sm'>Between these two lines</p>
          </div>
          <div className='flex h-8 items-center gap-4 text-sm'>
            <span>Left</span>
            <Separator orientation='vertical' />
            <span>Middle</span>
            <Separator orientation='vertical' />
            <span>Right</span>
          </div>
        </Section>
      </div>
    </main>
  )
}
